import React from "react";
import { MID, THINKING_LINE_OPACITY, thoughtFor } from "./geometry";
function Line({ g, from, to, className, opacity, style }) {
    return /*#__PURE__*/ React.createElement("line", {
        className: className,
        x1: from,
        y1: MID,
        x2: to,
        y2: MID,
        pathLength: 1,
        stroke: "currentColor",
        strokeWidth: g.stroke,
        strokeLinecap: "round",
        opacity: opacity,
        style: style
    });
}
const Track = ({ g })=>/*#__PURE__*/ React.createElement(Line, {
        g: g,
        from: g.start,
        to: g.end,
        opacity: g.track
    });
const Dots = ({ g })=>/*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("circle", {
        className: "dot dot-left",
        cx: g.start,
        cy: MID,
        r: g.dot,
        fill: "currentColor"
    }), /*#__PURE__*/ React.createElement("circle", {
        className: "dot dot-right",
        cx: g.end,
        cy: MID,
        r: g.dot,
        fill: "currentColor"
    }));
function Thinking({ g }) {
    const { rise, half } = thoughtFor(g);
    const thought = {
        ...g,
        stroke: g.stroke * 0.85
    };
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Line, {
        g: g,
        from: g.start,
        to: g.end,
        opacity: THINKING_LINE_OPACITY
    }), /*#__PURE__*/ React.createElement("g", {
        className: "thought",
        style: {
            ["--from"]: `${-rise}px`
        }
    }, /*#__PURE__*/ React.createElement(Line, {
        g: thought,
        from: MID - half,
        to: MID + half
    })), /*#__PURE__*/ React.createElement("g", {
        className: "thought thought-below",
        style: {
            ["--from"]: `${rise}px`
        }
    }, /*#__PURE__*/ React.createElement(Line, {
        g: thought,
        from: MID - half,
        to: MID + half
    })), /*#__PURE__*/ React.createElement(Dots, {
        g: g
    }));
}
function Writing({ g }) {
    // Words sit between the dots, clear of them.
    const from = g.start + g.dot + 1;
    const length = g.end - g.dot - 1 - from;
    const at = (fraction)=>from + length * fraction;
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Track, {
        g: g
    }), /*#__PURE__*/ React.createElement(Line, {
        g: g,
        from: at(0),
        to: at(0.34),
        className: "word word-1"
    }), /*#__PURE__*/ React.createElement(Line, {
        g: g,
        from: at(0.5),
        to: at(0.68),
        className: "word word-2"
    }), /*#__PURE__*/ React.createElement(Line, {
        g: g,
        from: at(0.84),
        to: at(1),
        className: "word word-3"
    }), /*#__PURE__*/ React.createElement(Dots, {
        g: g
    }));
}
function Progress({ g, value }) {
    const fraction = Math.min(100, Math.max(0, value)) / 100;
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Track, {
        g: g
    }), /*#__PURE__*/ React.createElement(Line, {
        g: g,
        from: g.start,
        to: g.end,
        className: "fill",
        style: {
            strokeDashoffset: 1 - fraction
        }
    }), /*#__PURE__*/ React.createElement("circle", {
        className: "dot",
        cx: g.start,
        cy: MID,
        r: g.dot,
        fill: "currentColor"
    }), /*#__PURE__*/ React.createElement("circle", {
        className: fraction === 1 ? "dot target target-reached" : "dot target",
        cx: g.end,
        cy: MID,
        r: g.dot - g.stroke / 2,
        fill: "currentColor",
        stroke: "currentColor",
        strokeWidth: g.stroke
    }));
}
function Halves({ g }) {
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Track, {
        g: g
    }), /*#__PURE__*/ React.createElement(Line, {
        g: g,
        from: g.start,
        to: MID,
        className: "half"
    }), /*#__PURE__*/ React.createElement(Line, {
        g: g,
        from: g.end,
        to: MID,
        className: "half"
    }), /*#__PURE__*/ React.createElement(Dots, {
        g: g
    }));
}
function Pen({ g }) {
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Track, {
        g: g
    }), /*#__PURE__*/ React.createElement(Line, {
        g: g,
        from: g.start,
        to: g.end,
        className: "pen"
    }), /*#__PURE__*/ React.createElement(Dots, {
        g: g
    }));
}
const DRAWINGS = {
    loading: (g)=>/*#__PURE__*/ React.createElement(Pen, {
            g: g
        }),
    thinking: (g)=>/*#__PURE__*/ React.createElement(Thinking, {
            g: g
        }),
    writing: (g)=>/*#__PURE__*/ React.createElement(Writing, {
            g: g
        }),
    reading: (g)=>/*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Track, {
            g: g
        }), /*#__PURE__*/ React.createElement(Line, {
            g: g,
            from: g.start,
            to: g.end,
            className: "cursor"
        }), /*#__PURE__*/ React.createElement(Dots, {
            g: g
        })),
    syncing: (g)=>/*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Track, {
            g: g
        }), /*#__PURE__*/ React.createElement(Line, {
            g: g,
            from: g.start,
            to: g.end,
            className: "pen"
        }), /*#__PURE__*/ React.createElement(Line, {
            g: g,
            from: g.end,
            to: g.start,
            className: "pen"
        }), /*#__PURE__*/ React.createElement(Dots, {
            g: g
        })),
    searching: (g)=>/*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Track, {
            g: g
        }), /*#__PURE__*/ React.createElement(Dots, {
            g: g
        }), /*#__PURE__*/ React.createElement("circle", {
            className: "lens",
            cx: g.start,
            cy: MID,
            r: g.dot * 1.75,
            fill: "none",
            stroke: "currentColor",
            strokeWidth: g.stroke,
            style: {
                ["--travel"]: `${g.end - g.start}px`
            }
        })),
    queued: (g)=>/*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Line, {
            g: g,
            from: g.start,
            to: g.end,
            className: "waiting"
        }), /*#__PURE__*/ React.createElement(Dots, {
            g: g
        })),
    progress: (g, { value })=>/*#__PURE__*/ React.createElement(Progress, {
            g: g,
            value: value
        }),
    done: (g)=>/*#__PURE__*/ React.createElement(Pen, {
            g: g
        }),
    failed: (g)=>/*#__PURE__*/ React.createElement("g", {
            className: "shake"
        }, /*#__PURE__*/ React.createElement(Halves, {
            g: g
        })),
    reconnecting: (g)=>/*#__PURE__*/ React.createElement(Halves, {
            g: g
        })
};
export function drawingFor(state, g, options) {
    return DRAWINGS[state](g, options);
}
